import { T } from 'consts/translationKeys'
import eventBus from 'services/event-bus/eventBus'
import { EVENT_TYPES } from 'services/event-bus/eventTypes'
import {
  getLoadingProgressFromLocalStorage,
  ILoadingProgressData,
  removeLoadingProgressFromLocalStorage,
  updateLoadingProgressDataInLocalStorage
} from 'utils/localStorage'

import { translate } from 'utils/translations'

interface IMessageRange {
  startThreshold: number
  endThreshold: number
  text: string
}

interface IProgressPhase {
  maxProgress: number
  baseStep: number
  baseDelay: number
  speedMultiplier: number
}

const TICK_INTERVAL_MS = 100
const MIN_UPDATE_DELAY_MS = 50
const TARGET_UPDATES_COUNT = 200
const STALL_PHASE_UPDATE_INTERVAL_MS = 3000
const COMPLETION_DELAY_MS = 1500
const STALL_THRESHOLD = 99
export const LOADING_FINAL_MANDATORY_VALUE = 100

const MESSAGE_RANGES: IMessageRange[] = [
  {
    startThreshold: 0,
    endThreshold: 10,
    text: translate(T.PREPARING_THE_VIDEO)
  },
  {
    startThreshold: 10,
    endThreshold: 20,
    text: translate(T.SETTING_UP_THE_SCENES)
  },
  {
    startThreshold: 20,
    endThreshold: 30,
    text: translate(T.PLANNING_THE_MOTION)
  },
  {
    startThreshold: 30,
    endThreshold: 40,
    text: translate(T.CREATING_THE_VIDEO)
  },
  {
    startThreshold: 40,
    endThreshold: 50,
    text: translate(T.RENDERING_FRAMES)
  },
  {
    startThreshold: 50,
    endThreshold: 60,
    text: translate(T.ADDING_MOVEMENT)
  },
  {
    startThreshold: 60,
    endThreshold: 70,
    text: translate(T.PUTTING_IT_TOGETHER)
  },
  { startThreshold: 70, endThreshold: 80, text: translate(T.FINAL_FRAMES) },
  { startThreshold: 80, endThreshold: 90, text: translate(T.ALMOST_READY) },
  {
    startThreshold: 90,
    endThreshold: 98,
    text: translate(T.FINISHING_THE_VIDEO)
  }
]

const STALL_MESSAGES = [
  translate(T.DRAMATIC_PAUSE),
  translate(T.SO_CLOSE_YOU_CAN_ALMOST_SEE_IT),
  translate(T.OKAY_THIS_ONE_DETAIL_IS_BEING_STUBBORN),
  translate(T.JUST_PRETENDING_TO_BE_SLOW_NOW)
]

const PROGRESS_PHASES: IProgressPhase[] = [
  { maxProgress: 15, baseStep: 1, baseDelay: 300, speedMultiplier: 1.0 },
  { maxProgress: 44, baseStep: 1, baseDelay: 300, speedMultiplier: 0.7 },
  { maxProgress: 79, baseStep: 0.8, baseDelay: 400, speedMultiplier: 0.4 },
  { maxProgress: 99, baseStep: 0.6, baseDelay: 400, speedMultiplier: 0.2 }
]

const DEFAULT_EXPECTED_DURATION_SECONDS = 90
const DEFAULT_EXPECTED_DURATION_IN_MILLISECONDS =
  DEFAULT_EXPECTED_DURATION_SECONDS * 1000

const EXPECTED_TOTAL_PERCENTAGE = 99

class GeneratorInstance {
  private progress: number
  private message: string = MESSAGE_RANGES[0].text
  private stallIndex: number = 0
  private timer: ReturnType<typeof setTimeout> | null = null
  private isActive: boolean = false
  private lastSavedDecile: number = 0
  private assetId: string

  constructor(
    private mediaItemId: string,
    initialAssetId: string | number,
    private expectedDurationInMilliseconds: number,
    initialProgress: number = 0,
    private useFakeAcceleration: boolean,
    private startTime: number = Date.now()
  ) {
    this.progress = initialProgress
    this.lastSavedDecile = Math.floor(initialProgress / 10)
    this.assetId = String(initialAssetId)
  }

  public start(): void {
    if (this.isActive) return

    this.isActive = true
    this.publish()
    this.timer = setTimeout(() => this.tick(), TICK_INTERVAL_MS)
  }

  public stop(): void {
    this.isActive = false
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.publish()
  }

  public getProgress(): number {
    return Math.floor(this.progress)
  }

  public getMessage(): string {
    return this.message
  }

  public getAssetId(): string {
    return this.assetId
  }

  public getMediaItemId(): string {
    return this.mediaItemId
  }

  private get currentDecile(): number {
    return this.progress === EXPECTED_TOTAL_PERCENTAGE
      ? EXPECTED_TOTAL_PERCENTAGE
      : Math.floor(this.progress / 10)
  }

  public setProgress(progress: number): void {
    this.progress = progress
    this.updateStatusMessage()
    this.publish()
  }

  public updateAssetId(assetId: string | number): void {
    this.assetId = String(assetId)
  }

  private get isStallPhase(): boolean {
    return this.progress > STALL_THRESHOLD
  }
  private get currentPhase() {
    return (
      PROGRESS_PHASES.find(p => this.progress < p.maxProgress) ||
      PROGRESS_PHASES.at(-1)!
    )
  }

  private tick(): void {
    if (!this.isActive || this.progress >= LOADING_FINAL_MANDATORY_VALUE) return

    if (this.useFakeAcceleration) {
      this.tickEased()
    } else {
      this.tickLinear()
    }

    // to resume in history in other tabs or in reload case in other tabs

    const currentDecile = this.currentDecile

    if (currentDecile > this.lastSavedDecile) {
      // read current data to preserve assetId that might have been updated by socket handlers
      updateLoadingProgressDataInLocalStorage({
        id: this.mediaItemId,
        assetId: this.assetId,
        progress: this.progress
      })
      this.lastSavedDecile = currentDecile
    }
  }
  // ease in out(manual) progress
  private tickEased(): void {
    if (this.isStallPhase) {
      this.handleStallPhase()
      return
    }

    this.calculateNextEasedProgress()
    this.updateStatusMessage()
    this.publish()

    const phase = this.currentPhase
    if (this.isActive) {
      this.timer = setTimeout(() => this.tick(), phase.baseDelay)
    }
  }
  // for images we use linear progress
  private tickLinear(): void {
    if (this.isStallPhase) {
      this.handleStallPhase()
      return
    }

    const elapsed = Date.now() - this.startTime
    const progressRatio = Math.min(
      elapsed / this.expectedDurationInMilliseconds,
      1.0
    )
    // Calculate target progress as whole number (0-99)
    const targetProgress = Math.floor(progressRatio * EXPECTED_TOTAL_PERCENTAGE)

    // Only update if we've moved to a new whole number
    if (targetProgress > Math.floor(this.progress)) {
      this.progress = targetProgress
      this.updateStatusMessage()
      this.publish()
    }

    // Continue ticking until we reach 99%
    if (this.progress < EXPECTED_TOTAL_PERCENTAGE) {
      // Calculate delay based on expected duration
      // Update more frequently to catch whole number changes
      const delay = Math.max(
        this.expectedDurationInMilliseconds / TARGET_UPDATES_COUNT,
        MIN_UPDATE_DELAY_MS
      )
      if (this.isActive) {
        this.timer = setTimeout(() => this.tick(), delay)
      }
    }
  }
  // for fake acceleration case, we need to manipulate the progress to make it slower or accelerate it depending on the phase
  private calculateNextEasedProgress(): void {
    const phase = this.currentPhase
    const elapsed = Date.now() - this.startTime
    const targetProgress = Math.min(
      (elapsed / this.expectedDurationInMilliseconds) *
        EXPECTED_TOTAL_PERCENTAGE,
      EXPECTED_TOTAL_PERCENTAGE
    )

    let step = phase.baseStep * phase.speedMultiplier
    if (this.progress < targetProgress) {
      // move faster if behind schedule( schedule is calc. according to the expected duration)
      const catchUpStep = Math.min(targetProgress - this.progress, step * 2)
      step = Math.max(step, catchUpStep)
    }

    this.progress = Math.min(this.progress + step, EXPECTED_TOTAL_PERCENTAGE)
  }

  private updateStatusMessage(): void {
    const range = MESSAGE_RANGES.find(
      range =>
        this.progress >= range.startThreshold &&
        this.progress < range.endThreshold
    )
    if (range) this.message = range.text
  }

  private handleStallPhase(): void {
    if (this.stallIndex < STALL_MESSAGES.length) {
      this.message = STALL_MESSAGES[this.stallIndex++]
      this.publish()
      if (this.isActive) {
        this.timer = setTimeout(
          () => this.tick(),
          STALL_PHASE_UPDATE_INTERVAL_MS
        )
      }
    }
  }

  private publish(): void {
    eventBus.publish(
      EVENT_TYPES.GENERATION_PROGRESS_UPDATE,
      this.mediaItemId,
      this.assetId,
      Math.floor(this.progress),
      this.message,
      this.isActive
    )
  }
}
// manager service
class GenerationProgressService {
  private activeGenerators = new Map<string, GeneratorInstance>()

  // use start only if you really started generation and need to start progress, for other cases, resume
  public start(
    mediaItemId: string,
    assetId: string | number,
    expectedDurationSeconds: number = DEFAULT_EXPECTED_DURATION_SECONDS,
    useFakeAcceleration: boolean = false,
    initialProgress: number
  ): void {
    if (!mediaItemId || expectedDurationSeconds <= 0) {
      return
    }
    const stringifiedAssetId = String(assetId)

    const existingGenerator = this.activeGenerators.get(mediaItemId)

    // if it exists check if we need to manually update its progress
    if (existingGenerator) {
      const currentProgress = existingGenerator.getProgress()
      if (initialProgress > currentProgress) {
        existingGenerator.setProgress(initialProgress)
      }
      return
    }

    const expectedDurationInMilliseconds = expectedDurationSeconds * 1000

    // only create a new generator if it doesn't exist
    const generator = new GeneratorInstance(
      mediaItemId,
      stringifiedAssetId,
      expectedDurationInMilliseconds,
      initialProgress,
      useFakeAcceleration
    )

    this.activeGenerators.set(mediaItemId, generator)
    // in case of reloading the page we need the finish time from ls to calc the left duration accoridng to the original finish time and not start it again
    const finishTime = Date.now() + expectedDurationInMilliseconds
    updateLoadingProgressDataInLocalStorage({
      id: mediaItemId,
      assetId: stringifiedAssetId,
      progress: initialProgress,
      finishTime,
      expectedDurationInMilliseconds,
      useFakeAcceleration
    })

    generator.start()
  }

  // used in reloading or opening parallel tab
  public resume(mediaItemId: string, initialAssetId?: string | number): void {
    const doesGeneratorExist = this.activeGenerators.has(mediaItemId)
    if (doesGeneratorExist) return

    const savedData = this.getValidatedSavedData(mediaItemId, initialAssetId)
    const {
      progress,
      finishTime,
      assetId,
      expectedDurationInMilliseconds,
      useFakeAcceleration
    } = savedData
    const duration =
      expectedDurationInMilliseconds ??
      DEFAULT_EXPECTED_DURATION_IN_MILLISECONDS

    const now = Date.now()
    let originalStartTime: number

    if (finishTime) {
      // if finishTime exists, calculate start time from it
      // ensure start time isn't in the future
      originalStartTime = Math.min(finishTime - duration, now)
    } else {
      // if finishTime is missing, calculate start time based on current progress
      const elapsedTime = (progress / EXPECTED_TOTAL_PERCENTAGE) * duration
      originalStartTime = now - elapsedTime
    }

    const generator = new GeneratorInstance(
      mediaItemId,
      String(assetId),
      duration,
      Math.min(progress, EXPECTED_TOTAL_PERCENTAGE),
      useFakeAcceleration ?? false,
      originalStartTime
    )

    this.activeGenerators.set(mediaItemId, generator)
    generator.start()
  }

  private getValidatedSavedData(
    mediaItemId: string,
    assetId?: string | number
  ): ILoadingProgressData {
    const defaultData = {
      id: mediaItemId,
      assetId: assetId ? String(assetId) : mediaItemId,
      progress: 0,
      finishTime: undefined,
      expectedDurationInMilliseconds: DEFAULT_EXPECTED_DURATION_IN_MILLISECONDS,
      useFakeAcceleration: false
    }
    const savedData = getLoadingProgressFromLocalStorage(mediaItemId)
    if (!(savedData instanceof Object)) {
      return defaultData
    }

    const validateData = <K extends keyof ILoadingProgressData>(
      key: K
    ): ILoadingProgressData[K] => savedData[key] ?? defaultData[key]

    return {
      id: validateData('id'),
      assetId: validateData('assetId'),
      progress: validateData('progress'),
      finishTime: validateData('finishTime'),
      expectedDurationInMilliseconds: validateData(
        'expectedDurationInMilliseconds'
      ),
      useFakeAcceleration: validateData('useFakeAcceleration')
    }
  }

  public getGeneratorIdPerAssetId(
    assetId?: string | number
  ): string | undefined {
    if (!assetId) return undefined

    const assetIdStr = String(assetId)

    const generators = Array.from(this.activeGenerators.values())
    const generator = generators.find(
      generator => generator.getAssetId() === assetIdStr
    )
    if (generator) {
      return generator.getMediaItemId()
    }

    const generationProgressData =
      getLoadingProgressFromLocalStorage(assetIdStr)
    return generationProgressData?.id ?? undefined
  }

  public stop({
    mediaItemId,
    assetId,
    shouldRemoveFromStorage = false
  }: {
    mediaItemId?: string
    assetId?: string | number
    shouldRemoveFromStorage: boolean
  }): void {
    const generatorId = mediaItemId ?? this.getGeneratorIdPerAssetId(assetId)

    if (!generatorId) return
    const generator = this.activeGenerators.get(generatorId)
    if (generator) {
      generator.stop()
      if (this.activeGenerators.has(generatorId)) {
        this.activeGenerators.delete(generatorId)
      }
      if (shouldRemoveFromStorage) {
        removeLoadingProgressFromLocalStorage(generatorId)
      }
    }
  }

  public complete({
    mediaItemId,
    assetId
  }: {
    mediaItemId?: string
    assetId?: string | number
  }): void {
    const generatorId = mediaItemId ?? this.getGeneratorIdPerAssetId(assetId)
    if (!generatorId) return
    const generator = this.activeGenerators.get(generatorId)
    if (generator) {
      generator.setProgress(LOADING_FINAL_MANDATORY_VALUE)
      // delay so user sees 100%
      setTimeout(
        () =>
          this.stop({ mediaItemId, assetId, shouldRemoveFromStorage: true }),
        COMPLETION_DELAY_MS
      )
    }
  }

  public getProgress(mediaItemId: string): number | null {
    const generator = this.activeGenerators.get(mediaItemId)
    return generator ? generator.getProgress() : null
  }

  public getMessage(mediaItemId: string): string | null {
    const generator = this.activeGenerators.get(mediaItemId)
    return generator ? generator.getMessage() : null
  }

  public isActive(mediaItemId: string): boolean {
    return this.activeGenerators.has(mediaItemId)
  }

  public updateAssetId(mediaItemId: string, assetId: string | number): void {
    const generator = this.activeGenerators.get(mediaItemId)
    if (generator) {
      generator.updateAssetId(assetId)
      updateLoadingProgressDataInLocalStorage({
        id: mediaItemId,
        assetId
      })
    }
  }

  public stopAll(): void {
    this.activeGenerators.forEach(generator => generator.stop())
    this.activeGenerators.clear()
  }
}

export const generationProgressService = new GenerationProgressService()
